import React, { useState, useEffect, useRef } from 'react';
import { pageSectionService, mediaService, pageService } from '../../services/api';
import {
  GripVertical, Eye, EyeOff, Plus, Trash2, Save, X, ChevronUp, ChevronDown,
  Type, LayoutGrid, BarChart3, Megaphone, Columns, Move, Users, BookOpen,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Image as ImageIcon, Search, ChevronDown as ChevronDownIcon
} from 'lucide-react';
import { sectionTemplates, defaultSection } from '../../utils/sectionTemplates';
import RichTextEditor from './RichTextEditor';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1').replace('/api/v1', '');

const templateIcons = {
  hero: Megaphone,
  content: Type,
  image_text: Columns,
  feature_grid: LayoutGrid,
  stats: BarChart3,
  cta: Megaphone,
  timeline: BookOpen,
  team: Users,
  faq: BookOpen,
};

const templates = sectionTemplates;

const templateSwatches = {
  hero: 'bg-gradient-to-r from-slate-900 to-red-900 text-white',
  content: 'bg-slate-100 text-slate-700',
  image_text: 'bg-red-100 text-red-700',
  feature_grid: 'bg-slate-900 text-white',
  stats: 'bg-red-50 text-red-700',
  cta: 'bg-slate-900 text-white',
  timeline: 'bg-indigo-100 text-indigo-700',
  team: 'bg-rose-100 text-rose-700',
  faq: 'bg-amber-100 text-amber-700',
};

const cloneDefaults = (template) => {
  if (!template?.defaults) return { meta: {} };
  try {
    const cloned = JSON.parse(JSON.stringify(template.defaults));
    if (!cloned.meta) cloned.meta = {};
    return cloned;
  } catch (error) {
    console.error('Template clone failed', error);
    return { ...(template.defaults || {}), meta: template.defaults?.meta || {} };
  }
};

const cloneMeta = (meta) => {
  if (!meta) return {};
  try {
    return JSON.parse(JSON.stringify(meta));
  } catch (error) {
    console.error('Meta clone failed', error);
    return { ...(meta || {}) };
  }
};

const defaultMetaItems = {
  feature: { icon: '✨', title: '', desc: '' },
  stat: { label: '', value: '' },
  timeline: { year: new Date().getFullYear().toString(), title: '', desc: '' },
  person: { name: '', title: '', photo: '', bio: '' },
  faq: { q: '', a: '' },
};

const bgOptions = [
  { value: 'white', label: 'White', class: 'bg-white' },
  { value: 'slate-50', label: 'Light Grey', class: 'bg-slate-50' },
  { value: 'red-50', label: 'Light Green', class: 'bg-red-50' },
  { value: 'slate-900', label: 'Dark Corporate', class: 'bg-slate-900 text-white' },
];

function ModernPageBuilder({ pageSlug = '' }) {
  const [sections, setSections] = useState([]);
  const [selectedPage, setSelectedPage] = useState(pageSlug || 'home');
  const [editingId, setEditingId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [availablePages, setAvailablePages] = useState([]);
  const [pageSearch, setPageSearch] = useState('');
  const [pageDropdownOpen, setPageDropdownOpen] = useState(false);
  const pageDropdownRef = useRef(null);
  const [form, setForm] = useState(() => {
    const defaults = cloneDefaults(defaultSection);
    return { ...defaults, page: pageSlug || 'home', section_type: defaultSection.type, is_active: true, sort_order: 0 };
  });

  useEffect(() => {
    if (pageSlug && pageSlug !== selectedPage) {
      setSelectedPage(pageSlug);
      setForm(prev => ({ ...prev, page: pageSlug }));
    }
  }, [pageSlug]);

  useEffect(() => { loadSections(); loadPages(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(e.target)) {
        setPageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadPages = async () => {
    try {
      const res = await pageService.index({ per_page: 200 });
      const pages = Array.isArray(res) ? res : res?.data || [];
      setAvailablePages(pages.map(p => ({ slug: p.slug, title: p.title || p.slug })));
    } catch (e) {
      setAvailablePages([]);
    }
  };

  const loadSections = async () => {
    setSaveError('');
    try {
      const res = await pageSectionService.index();
      console.log('Sections API response:', res);
      const data = Array.isArray(res) ? res : res?.data || [];
      setSections(data);
      if (!data.length) {
        console.log('No sections returned for any page.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load sections.';
      setSaveError(msg);
      console.error('Load sections error:', err);
    }
  };

  const pageSections = sections
    .filter((s) => s.page === selectedPage)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const resetForm = (type = defaultSection.type) => {
    const template = templates.find((t) => t.type === type) || defaultSection;
    const defaults = cloneDefaults(template);
    setEditingId('new');
    setSaveSuccess('');
    setSaveError('');
    setForm({ ...defaults, page: selectedPage, section_type: template.type, sort_order: pageSections.length + 1, is_active: true });
  };

  const editSection = (section) => {
    setEditingId(section.id);
    setSaveSuccess('');
    setSaveError('');
    // Strip id/created_at/updated_at so they don't get sent back in body
    const { id, created_at, updated_at, ...editable } = section;
    const template = templates.find((t) => t.type === section.section_type);
    if (template) {
      const defaults = cloneDefaults(template);
      const mergedMeta = { ...(defaults.meta || {}), ...cloneMeta(editable.meta) };
      setForm({ ...defaults, ...editable, meta: mergedMeta });
    } else {
      setForm({ ...editable, meta: cloneMeta(editable.meta) });
    }
    setSelectedPage(section.page);
  };

  const saveSection = async (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }

    setSaveSuccess('');
    setSaveError('');

    try {
      if (!form.page) {
        setSaveError('Page slug is missing. Please pick a page.');
        return;
      }

      const payload = {
        ...form,
        is_active: !!form.is_active,
        sort_order: parseInt(form.sort_order || 0, 10),
        meta: form.meta && Object.keys(form.meta).length ? form.meta : null,
      };

      const isUpdate = editingId && editingId !== 'new';
      if (isUpdate) {
        await pageSectionService.update(editingId, payload);
      } else {
        await pageSectionService.store(payload);
      }

      setSaveSuccess(isUpdate ? 'Section updated successfully.' : 'Section created successfully.');
      if (!isUpdate) {
        resetForm(form.section_type);
      }
      await loadSections();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not save section.';
      setSaveError(msg);
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await pageSectionService.destroy(id);
    loadSections();
  };

  const makeAbsoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    setSaveError('');
    console.log('Starting image upload...', file.name);
    try {
      const media = await mediaService.upload(file, 'page-builder');
      console.log('Raw upload response:', media);
      const rawUrl = media?.url || media?.path || '';
      const url = makeAbsoluteUrl(rawUrl);
      if (!url) {
        setSaveError('Upload succeeded but no image URL was returned.');
      } else {
        setForm((prev) => ({ ...prev, image: url }));
        console.log('Image URL set in form:', url);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Image upload failed.';
      setSaveError(msg);
      console.error('Upload error:', err);
      alert('Upload failed: ' + msg);
    }
    setUploading(false);
  };

  const moveSection = async (index, direction) => {
    const arr = [...pageSections];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    const updates = arr.map((s, i) => ({ ...s, sort_order: i + 1 }));
    setSections((prev) => prev.map((s) => updates.find((u) => u.id === s.id) || s));
    for (const s of updates) {
      try { await pageSectionService.update(s.id, { sort_order: s.sort_order }); } catch (e) {}
    }
  };

  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, idx) => { e.preventDefault(); };
  const onDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) return;
    const arr = [...pageSections];
    const [removed] = arr.splice(dragIdx, 1);
    arr.splice(dropIdx, 0, removed);
    const updates = arr.map((s, i) => ({ ...s, sort_order: i + 1 }));
    setSections((prev) => prev.map((s) => updates.find((u) => u.id === s.id) || s));
    for (const s of updates) {
      try { await pageSectionService.update(s.id, { sort_order: s.sort_order }); } catch (e) {}
    }
    setDragIdx(null);
  };

  const toggleActive = async (section) => {
    try {
      await pageSectionService.update(section.id, { is_active: !section.is_active });
      loadSections();
    } catch (e) {}
  };

  const handleMetaChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      meta: { ...(prev.meta || {}), [key]: value },
    }));
  };

  const handleMetaArrayChange = (listKey, index, field, value) => {
    setForm((prev) => {
      const list = [...(prev.meta?.[listKey] || [])];
      list[index] = { ...(list[index] || {}), [field]: value };
      return {
        ...prev,
        meta: { ...(prev.meta || {}), [listKey]: list },
      };
    });
  };

  const addMetaItem = (listKey, typeKey) => {
    setForm((prev) => {
      const list = [...(prev.meta?.[listKey] || [])];
      const base = defaultMetaItems[typeKey]
        ? JSON.parse(JSON.stringify(defaultMetaItems[typeKey]))
        : {};
      list.push(base);
      return {
        ...prev,
        meta: { ...(prev.meta || {}), [listKey]: list },
      };
    });
  };

  const removeMetaItem = (listKey, index) => {
    setForm((prev) => {
      const list = [...(prev.meta?.[listKey] || [])];
      list.splice(index, 1);
      return {
        ...prev,
        meta: { ...(prev.meta || {}), [listKey]: list },
      };
    });
  };

  const changeSectionType = (type) => {
    const template = templates.find((t) => t.type === type) || defaultSection;
    const defaults = cloneDefaults(template);
    setForm((prev) => ({
      ...defaults,
      page: prev.page,
      section_type: type,
      sort_order: prev.sort_order,
      is_active: prev.is_active ?? true,
    }));
  };

  const renderMetaControls = () => {
    const meta = form.meta || {};
    const SectionLabel = ({ children }) => (
      <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">{children}</p>
    );

    switch (form.section_type) {
      case 'hero':
        return (
          <div className="space-y-3">
            <div>
              <SectionLabel>Hero Badge</SectionLabel>
              <input
                value={meta.badge || ''}
                onChange={(e) => handleMetaChange('badge', e.target.value)}
                placeholder="Badge text"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <SectionLabel>Align Content</SectionLabel>
                <select
                  value={meta.align || 'center'}
                  onChange={(e) => handleMetaChange('align', e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-400 pt-5">
                <input
                  type="checkbox"
                  checked={!!meta.overlay}
                  onChange={(e) => handleMetaChange('overlay', e.target.checked)}
                />
                Enable dark overlay
              </label>
            </div>
          </div>
        );

      case 'content': {
        const contentAlignOptions = [
          { value: 'left', icon: AlignLeft, label: 'Left' },
          { value: 'center', icon: AlignCenter, label: 'Center' },
          { value: 'right', icon: AlignRight, label: 'Right' },
          { value: 'justify', icon: AlignJustify, label: 'Justify' },
        ];
        const imgPositionOptions = ['none', 'top', 'bottom', 'left', 'right', 'full-width', 'background'];
        const imgSizeOptions = ['small', 'medium', 'large', 'full'];
        const imgRadiusOptions = ['none', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'];
        const widthOptions = ['narrow', 'medium', 'wide', 'full'];
        return (
          <div className="space-y-4">
            {/* Content Alignment */}
            <div>
              <SectionLabel>Content Alignment</SectionLabel>
              <div className="flex gap-1 mt-1">
                {contentAlignOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleMetaChange('contentAlign', opt.value)}
                      className={`flex-1 flex items-center justify-center py-2 rounded-lg border transition ${(meta.contentAlign || 'left') === opt.value ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                      title={opt.label}
                    >
                      <Icon size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Max Width */}
            <div>
              <SectionLabel>Content Width</SectionLabel>
              <div className="grid grid-cols-4 gap-1 mt-1">
                {widthOptions.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => handleMetaChange('contentWidth', w)}
                    className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.contentWidth || 'medium') === w ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>

            {/* Image Position */}
            <div>
              <SectionLabel>Image Position</SectionLabel>
              <div className="grid grid-cols-4 gap-1 mt-1">
                {imgPositionOptions.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleMetaChange('imagePosition', p)}
                    className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.imagePosition || 'none') === p ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                  >
                    {p === 'full-width' ? 'full' : p}
                  </button>
                ))}
              </div>
            </div>

            {/* Image Size - only show if image position is not none/background */}
            {meta.imagePosition && meta.imagePosition !== 'none' && meta.imagePosition !== 'background' && (
              <div>
                <SectionLabel>Image Size</SectionLabel>
                <div className="grid grid-cols-4 gap-1 mt-1">
                  {imgSizeOptions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleMetaChange('imageSize', s)}
                      className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.imageSize || 'large') === s ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Image Aspect Ratio */}
            {meta.imagePosition && meta.imagePosition !== 'none' && meta.imagePosition !== 'background' && (
              <div>
                <SectionLabel>Image Aspect Ratio</SectionLabel>
                <select
                  value={meta.imageRatio || 'auto'}
                  onChange={(e) => handleMetaChange('imageRatio', e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                >
                  <option value="auto">Auto (original)</option>
                  <option value="square">Square (1:1)</option>
                  <option value="wide">Wide (16:9)</option>
                  <option value="tall">Tall (4:5)</option>
                  <option value="landscape">Landscape (3:2)</option>
                  <option value="portrait">Portrait (2:3)</option>
                </select>
              </div>
            )}

            {/* Image Rounded Corners */}
            {meta.imagePosition && meta.imagePosition !== 'none' && meta.imagePosition !== 'background' && (
              <div>
                <SectionLabel>Image Corner Radius</SectionLabel>
                <div className="grid grid-cols-4 gap-1 mt-1">
                  {imgRadiusOptions.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => handleMetaChange('imageRadius', r)}
                      className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.imageRadius || 'xl') === r ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Image as Background Overlay */}
            {meta.imagePosition === 'background' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <input type="checkbox" checked={!!meta.overlay} onChange={(e) => handleMetaChange('overlay', e.target.checked)} />
                  Dark overlay for text readability
                </label>
                <div>
                  <SectionLabel>Overlay Opacity</SectionLabel>
                  <input
                    type="range" min="0" max="90" step="10"
                    value={meta.overlayOpacity || '50'}
                    onChange={(e) => handleMetaChange('overlayOpacity', e.target.value)}
                    className="w-full mt-1"
                  />
                  <span className="text-[10px] text-slate-500">{meta.overlayOpacity || '50'}%</span>
                </div>
              </div>
            )}

            {/* Padding */}
            <div>
              <SectionLabel>Section Padding</SectionLabel>
              <select
                value={meta.padding || 'normal'}
                onChange={(e) => handleMetaChange('padding', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              >
                <option value="none">None (tight)</option>
                <option value="sm">Small</option>
                <option value="normal">Normal</option>
                <option value="lg">Large</option>
                <option value="xl">Extra Large</option>
              </select>
            </div>
          </div>
        );
      }

      case 'image_text': {
        const contentAlignOptions = [
          { value: 'left', icon: AlignLeft, label: 'Left' },
          { value: 'center', icon: AlignCenter, label: 'Center' },
          { value: 'right', icon: AlignRight, label: 'Right' },
          { value: 'justify', icon: AlignJustify, label: 'Justify' },
        ];
        const imgSizeOptions = ['small', 'medium', 'large', 'full'];
        const imgRadiusOptions = ['none', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'];
        const splitOptions = ['50/50', '60/40', '40/60', '70/30', '30/70'];
        return (
          <div className="space-y-4">
            {/* Layout Direction */}
            <div>
              <SectionLabel>Image Position</SectionLabel>
              <div className="grid grid-cols-2 gap-1 mt-1">
                <button type="button" onClick={() => handleMetaChange('reverse', false)} className={`py-2 rounded-lg text-xs font-semibold border transition ${!meta.reverse ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}>Image Left</button>
                <button type="button" onClick={() => handleMetaChange('reverse', true)} className={`py-2 rounded-lg text-xs font-semibold border transition ${meta.reverse ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}>Image Right</button>
              </div>
            </div>

            {/* Column Split */}
            <div>
              <SectionLabel>Column Split (Image / Text)</SectionLabel>
              <select
                value={meta.split || '50/50'}
                onChange={(e) => handleMetaChange('split', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              >
                {splitOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Content Alignment */}
            <div>
              <SectionLabel>Text Alignment</SectionLabel>
              <div className="flex gap-1 mt-1">
                {contentAlignOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleMetaChange('contentAlign', opt.value)}
                      className={`flex-1 flex items-center justify-center py-2 rounded-lg border transition ${(meta.contentAlign || 'left') === opt.value ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                      title={opt.label}
                    >
                      <Icon size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Image Size */}
            <div>
              <SectionLabel>Image Size</SectionLabel>
              <div className="grid grid-cols-4 gap-1 mt-1">
                {imgSizeOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleMetaChange('imageSize', s)}
                    className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.imageSize || 'large') === s ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Image Aspect Ratio */}
            <div>
              <SectionLabel>Image Aspect Ratio</SectionLabel>
              <select
                value={meta.imageRatio || 'auto'}
                onChange={(e) => handleMetaChange('imageRatio', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              >
                <option value="auto">Auto (original)</option>
                <option value="square">Square (1:1)</option>
                <option value="wide">Wide (16:9)</option>
                <option value="tall">Tall (4:5)</option>
                <option value="landscape">Landscape (3:2)</option>
                <option value="portrait">Portrait (2:3)</option>
              </select>
            </div>

            {/* Image Rounded Corners */}
            <div>
              <SectionLabel>Image Corner Radius</SectionLabel>
              <div className="grid grid-cols-4 gap-1 mt-1">
                {imgRadiusOptions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => handleMetaChange('imageRadius', r)}
                    className={`py-2 rounded-lg text-[10px] uppercase font-semibold border transition ${(meta.imageRadius || '2xl') === r ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Vertical Alignment */}
            <div>
              <SectionLabel>Vertical Alignment</SectionLabel>
              <div className="grid grid-cols-3 gap-1 mt-1">
                <button type="button" onClick={() => handleMetaChange('vAlign', 'top')} className={`py-2 rounded-lg text-xs font-semibold border transition ${(meta.vAlign || 'center') === 'top' ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}>Top</button>
                <button type="button" onClick={() => handleMetaChange('vAlign', 'center')} className={`py-2 rounded-lg text-xs font-semibold border transition ${(meta.vAlign || 'center') === 'center' ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}>Center</button>
                <button type="button" onClick={() => handleMetaChange('vAlign', 'bottom')} className={`py-2 rounded-lg text-xs font-semibold border transition ${(meta.vAlign || 'center') === 'bottom' ? 'border-red-500 bg-red-600/10 text-red-400' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}>Bottom</button>
              </div>
            </div>

            {/* Image Shadow */}
            <div>
              <SectionLabel>Image Shadow</SectionLabel>
              <select
                value={meta.imageShadow || 'lg'}
                onChange={(e) => handleMetaChange('imageShadow', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              >
                <option value="none">None</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra Large</option>
              </select>
            </div>
          </div>
        );
      }

      case 'cta':
        return (
          <div>
            <SectionLabel>Callout Badge</SectionLabel>
            <input
              value={meta.badge || ''}
              onChange={(e) => handleMetaChange('badge', e.target.value)}
              placeholder="Badge text"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
            />
          </div>
        );

      case 'feature_grid': {
        const items = meta.items || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Feature Cards</SectionLabel>
              <button type="button" onClick={() => addMetaItem('items', 'feature')} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">
                <Plus size={12} /> Add
              </button>
            </div>
            {items.length === 0 && <p className="text-xs text-slate-500">No feature cards yet.</p>}
            {items.map((item, idx) => (
              <div key={idx} className="border border-slate-800 rounded-lg p-3 space-y-2 bg-slate-950/30">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold">Card {idx + 1}</span>
                  <button type="button" onClick={() => removeMetaItem('items', idx)} className="ml-auto text-red-400 hover:text-red-300 text-[10px] uppercase">Remove</button>
                </div>
                <input value={item.icon || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'icon', e.target.value)} placeholder="Icon emoji" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <input value={item.title || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'title', e.target.value)} placeholder="Title" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <textarea value={item.desc || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'desc', e.target.value)} placeholder="Description" rows={2} className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
              </div>
            ))}
          </div>
        );
      }

      case 'stats': {
        const stats = meta.stats || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Metrics</SectionLabel>
              <button type="button" onClick={() => addMetaItem('stats', 'stat')} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">
                <Plus size={12} /> Add
              </button>
            </div>
            {stats.length === 0 && <p className="text-xs text-slate-500">No stats yet.</p>}
            {stats.map((stat, idx) => (
              <div key={idx} className="border border-slate-800 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-2 bg-slate-950/30">
                <input value={stat.label || ''} onChange={(e) => handleMetaArrayChange('stats', idx, 'label', e.target.value)} placeholder="Label" className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <div className="flex gap-2">
                  <input value={stat.value || ''} onChange={(e) => handleMetaArrayChange('stats', idx, 'value', e.target.value)} placeholder="Value" className="flex-1 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                  <button type="button" onClick={() => removeMetaItem('stats', idx)} className="px-2 text-red-400 text-xs uppercase">Remove</button>
                </div>
              </div>
            ))}
          </div>
        );
      }

      case 'timeline': {
        const items = meta.items || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Timeline Events</SectionLabel>
              <button type="button" onClick={() => addMetaItem('items', 'timeline')} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">
                <Plus size={12} /> Add
              </button>
            </div>
            {items.length === 0 && <p className="text-xs text-slate-500">No milestones yet.</p>}
            {items.map((item, idx) => (
              <div key={idx} className="border border-slate-800 rounded-lg p-3 space-y-2 bg-slate-950/30">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold">Milestone {idx + 1}</span>
                  <button type="button" onClick={() => removeMetaItem('items', idx)} className="ml-auto text-red-400 hover:text-red-300 text-[10px] uppercase">Remove</button>
                </div>
                <input value={item.year || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'year', e.target.value)} placeholder="Year" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <input value={item.title || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'title', e.target.value)} placeholder="Title" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <textarea value={item.desc || ''} onChange={(e) => handleMetaArrayChange('items', idx, 'desc', e.target.value)} placeholder="Description" rows={2} className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
              </div>
            ))}
          </div>
        );
      }

      case 'team': {
        const people = meta.people || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Team Members</SectionLabel>
              <button type="button" onClick={() => addMetaItem('people', 'person')} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">
                <Plus size={12} /> Add
              </button>
            </div>
            {people.length === 0 && <p className="text-xs text-slate-500">No members yet.</p>}
            {people.map((person, idx) => (
              <div key={idx} className="border border-slate-800 rounded-lg p-3 space-y-2 bg-slate-950/30">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold">Member {idx + 1}</span>
                  <button type="button" onClick={() => removeMetaItem('people', idx)} className="ml-auto text-red-400 hover:text-red-300 text-[10px] uppercase">Remove</button>
                </div>
                <input value={person.name || ''} onChange={(e) => handleMetaArrayChange('people', idx, 'name', e.target.value)} placeholder="Name" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <input value={person.title || ''} onChange={(e) => handleMetaArrayChange('people', idx, 'title', e.target.value)} placeholder="Role" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <input value={person.photo || ''} onChange={(e) => handleMetaArrayChange('people', idx, 'photo', e.target.value)} placeholder="Photo URL" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <textarea value={person.bio || ''} onChange={(e) => handleMetaArrayChange('people', idx, 'bio', e.target.value)} placeholder="Short bio" rows={2} className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
              </div>
            ))}
          </div>
        );
      }

      case 'faq': {
        const faqs = meta.faqs || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>FAQ Items</SectionLabel>
              <button type="button" onClick={() => addMetaItem('faqs', 'faq')} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">
                <Plus size={12} /> Add
              </button>
            </div>
            {faqs.length === 0 && <p className="text-xs text-slate-500">No FAQs yet.</p>}
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-slate-800 rounded-lg p-3 space-y-2 bg-slate-950/30">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold">FAQ {idx + 1}</span>
                  <button type="button" onClick={() => removeMetaItem('faqs', idx)} className="ml-auto text-red-400 hover:text-red-300 text-[10px] uppercase">Remove</button>
                </div>
                <input value={faq.q || ''} onChange={(e) => handleMetaArrayChange('faqs', idx, 'q', e.target.value)} placeholder="Question" className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
                <textarea value={faq.a || ''} onChange={(e) => handleMetaArrayChange('faqs', idx, 'a', e.target.value)} placeholder="Answer" rows={2} className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200" />
              </div>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const metaControls = renderMetaControls();

  return (
    <div className="modern-page-builder space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Modern Page Builder</h2>
          <p className="text-slate-400 text-sm mt-1">Drag sections to reorder. Click to edit. Toggle preview to see the live layout.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${previewMode ? 'bg-red-600/20 text-red-400 border border-red-600/30' : 'bg-slate-800 text-slate-300 border border-slate-700'}`}
          >
            {previewMode ? <Eye size={16} /> : <EyeOff size={16} />}
            {previewMode ? 'Preview On' : 'Preview Off'}
          </button>
        </div>
      </div>

      {(saveSuccess || saveError) && (
        <div className={`rounded-lg px-4 py-3 text-sm font-medium ${saveSuccess ? 'bg-red-900/30 text-red-300 border border-red-700/40' : 'bg-red-900/30 text-red-300 border border-red-700/40'}`}>
          {saveSuccess || saveError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left - Templates */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Page Slug</label>
            <div className="relative" ref={pageDropdownRef}>
              <div
                onClick={() => { setPageDropdownOpen(!pageDropdownOpen); setPageSearch(''); }}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 cursor-pointer flex items-center justify-between hover:border-slate-600 transition"
              >
                <span className={selectedPage ? 'text-slate-200' : 'text-slate-500'}>{selectedPage || 'Select a page...'}</span>
                <ChevronDownIcon size={14} className={`text-slate-500 transition ${pageDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              {pageDropdownOpen && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl overflow-hidden">
                  <div className="relative border-b border-slate-800">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      autoFocus
                      value={pageSearch}
                      onChange={(e) => setPageSearch(e.target.value)}
                      placeholder="Search pages..."
                      className="w-full bg-slate-950 pl-8 pr-3 py-2 text-sm text-slate-200 focus:outline-none border-0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && pageSearch.trim()) {
                          const newSlug = pageSearch.trim().toLowerCase().replace(/\s+/g, '-');
                          setSelectedPage(newSlug);
                          setForm((p) => ({ ...p, page: newSlug }));
                          setPageDropdownOpen(false);
                        }
                      }}
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {(() => {
                      const sectionSlugs = [...new Set(sections.map(s => s.page))];
                      const pageMap = new Map();
                      availablePages.forEach(p => pageMap.set(p.slug, p.title));
                      sectionSlugs.forEach(slug => { if (!pageMap.has(slug)) pageMap.set(slug, slug); });
                      const allPages = Array.from(pageMap.entries()).map(([slug, title]) => ({ slug, title }));
                      const filtered = allPages.filter(p =>
                        p.slug.toLowerCase().includes(pageSearch.toLowerCase()) ||
                        p.title.toLowerCase().includes(pageSearch.toLowerCase())
                      );
                      if (filtered.length === 0 && pageSearch.trim()) {
                        return (
                          <button
                            onClick={() => {
                              const newSlug = pageSearch.trim().toLowerCase().replace(/\s+/g, '-');
                              setSelectedPage(newSlug);
                              setForm((p) => ({ ...p, page: newSlug }));
                              setPageDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-slate-800 transition"
                          >
                            + Create new page: "{pageSearch.trim().toLowerCase().replace(/\s+/g, '-')}"
                          </button>
                        );
                      }
                      return filtered.map((p) => (
                        <button
                          key={p.slug}
                          onClick={() => {
                            setSelectedPage(p.slug);
                            setForm((prev) => ({ ...prev, page: p.slug }));
                            setPageDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm transition ${p.slug === selectedPage ? 'bg-red-600/10 text-red-400' : 'text-slate-300 hover:bg-slate-800'}`}
                        >
                          <div className="font-medium">{p.slug}</div>
                          {p.title !== p.slug && <div className="text-[10px] text-slate-500">{p.title}</div>}
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Search or type a new page slug to create.</p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Add Section</h3>
            <div className="space-y-2">
              {templates.map((t) => {
                const Icon = templateIcons[t.type] || LayoutGrid;
                const swatch = templateSwatches[t.type] || 'bg-slate-800 text-slate-200';
                return (
                  <button
                    key={t.type}
                    onClick={() => resetForm(t.type)}
                    className="w-full flex items-center gap-3 text-left border border-slate-800 rounded-lg p-3 hover:border-red-600/40 hover:bg-slate-800/60 transition"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${swatch}`}>
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-200">{t.label}</div>
                      <div className="text-[10px] text-slate-500">{t.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Center - Canvas */}
        <div className="lg:col-span-6 space-y-4">
          {editingId !== null && !previewMode && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-200">{editingId === 'new' ? 'New Section' : 'Edit Section'}</h3>
                <span className="px-2 py-0.5 rounded bg-red-600/10 text-red-400 text-[10px] font-bold uppercase border border-red-600/20">{form.section_type}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input value={form.page} onChange={(e) => setForm({ ...form, page: e.target.value })} placeholder="Page slug" className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                <select value={form.section_type} onChange={(e) => changeSectionType(e.target.value)} className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500">
                  {templates.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
                </select>
              </div>

              <input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Section Title" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
              <input value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Subtitle / Eyebrow" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
              <div>
                <label className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-1 block">Content (Rich Text Editor)</label>
                <RichTextEditor
                  value={form.content || ''}
                  onChange={(val) => setForm({ ...form, content: val })}
                  placeholder="Write your content here..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <input value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                  <input value={form.media_url || ''} onChange={(e) => setForm({ ...form, media_url: e.target.value })} placeholder="Video / Media URL" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImage(e.target.files[0]); e.target.value = ''; }} />
                    <span className="bg-slate-800 border border-slate-700 rounded px-2 py-1 hover:bg-slate-700 transition">{uploading ? 'Uploading...' : 'Upload Image'}</span>
                  </label>
                  {form.image && (
                    <div>
                      <img src={form.image} alt="" className="h-24 w-full object-cover rounded-lg border border-slate-700" onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load image'; }} />
                      <p className="text-[10px] text-slate-500 mt-1 truncate">{form.image}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <input value={form.button_text || ''} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="Button Text" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                  <input value={form.button_link || ''} onChange={(e) => setForm({ ...form, button_link: e.target.value })} placeholder="Button Link" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                  <select value={form.bg_color || 'white'} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500">
                    {bgOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} placeholder="Sort Order" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded" /> Active
                  </label>
                </div>
              </div>

              {metaControls && (
                <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Section Specific Options</p>
                  {metaControls}
                </div>
              )}

              <div className="flex gap-2">
                <button type="button" onClick={(e) => saveSection(e)} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                  <Save size={14} /> {editingId === 'new' ? 'Save Section' : 'Update Section'}
                </button>
                <button type="button" onClick={() => resetForm()} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium transition">
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 min-h-[400px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-200">Page Canvas: <span className="text-red-400">{selectedPage}</span></h3>
              <span className="text-[10px] text-slate-500">{pageSections.length} sections</span>
            </div>

            {pageSections.length === 0 && (
              <div className="border-2 border-dashed border-slate-800 rounded-xl p-12 text-center">
                <Move size={32} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-500 text-sm">No sections yet. Pick a template from the left to start building.</p>
              </div>
            )}

            <div className="space-y-2">
              {pageSections.map((s, idx) => {
                const meta = s.meta || {};
                const alignmentClass = meta.align === 'left' ? 'text-left' : meta.align === 'right' ? 'text-right' : 'text-center';
                const featureItems = meta.items && Array.isArray(meta.items) && meta.items.length ? meta.items : [];
                const statsItems = meta.stats && Array.isArray(meta.stats) && meta.stats.length ? meta.stats : [];
                const faqItems = meta.faqs && Array.isArray(meta.faqs) && meta.faqs.length ? meta.faqs : [];
                const teamItems = meta.people && Array.isArray(meta.people) && meta.people.length ? meta.people : [];

                return (
                  <div
                    key={s.id}
                    draggable={!previewMode}
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragOver={(e) => onDragOver(e, idx)}
                    onDrop={(e) => onDrop(e, idx)}
                    className={`group relative border rounded-xl transition ${
                      previewMode
                        ? s.bg_color === 'slate-900' ? 'bg-slate-900 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-900'
                        : 'bg-slate-950 border-slate-800 hover:border-red-600/30'
                    } ${!s.is_active && !previewMode ? 'opacity-50' : ''}`}
                  >
                    {!previewMode && (
                      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800/60">
                        <GripVertical size={14} className="text-slate-600 cursor-grab" />
                        <span className="text-[10px] uppercase font-bold text-slate-500">{s.section_type}</span>
                        <span className="text-[10px] text-slate-500">#{s.sort_order}</span>
                        <span className="text-[10px] text-slate-600 truncate flex-1 ml-1">{s.title || 'Untitled'}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-800 text-slate-500 disabled:opacity-30" title="Move up"><ChevronUp size={14} /></button>
                          <button onClick={() => moveSection(idx, 1)} disabled={idx === pageSections.length - 1} className="p-1 rounded hover:bg-slate-800 text-slate-500 disabled:opacity-30" title="Move down"><ChevronDown size={14} /></button>
                          <button onClick={() => toggleActive(s)} className={`p-1 rounded text-xs font-medium ${s.is_active ? 'text-red-400 hover:bg-red-600/10' : 'text-slate-500 hover:bg-slate-800'}`} title="Toggle active">{s.is_active ? 'On' : 'Off'}</button>
                          <button onClick={() => editSection(s)} className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition" title="Edit section">
                            <Eye size={12} /> Edit
                          </button>
                          <button onClick={() => deleteSection(s.id)} className="p-1 rounded hover:bg-red-600/10 text-red-400" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    )}

                    <div className={`p-4 ${previewMode ? '' : 'pointer-events-none select-none'} preview-content`}>
                      {s.section_type === 'hero' && (
                        <div className={`${alignmentClass} py-8 space-y-2`}>
                          {meta.badge && <span className="text-xs tracking-wide uppercase text-red-400 font-semibold">{meta.badge}</span>}
                          <h2 className="text-2xl font-bold">{s.title}</h2>
                          {s.subtitle && <p className="text-sm text-slate-400">{s.subtitle}</p>}
                          {s.content && <div className="mt-2 text-sm opacity-80" dangerouslySetInnerHTML={{ __html: s.content || '' }} />}
                          {s.image && <img src={s.image} alt="" className="w-full h-40 object-cover rounded-lg mt-4" />}
                          {s.button_text && <span className="inline-block mt-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm">{s.button_text}</span>}
                        </div>
                      )}

                      {s.section_type === 'content' && (() => {
                        const align = meta.contentAlign || 'left';
                        const width = meta.contentWidth || 'medium';
                        const imgPos = meta.imagePosition || 'none';
                        const imgSize = meta.imageSize || 'large';
                        const imgRadius = meta.imageRadius || 'xl';
                        const padding = meta.padding || 'normal';
                        const widthMap = { narrow: 'max-w-2xl', medium: 'max-w-4xl', wide: 'max-w-5xl', full: 'max-w-7xl' };
                        const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right', justify: 'text-justify' };
                        const paddingMap = { none: 'py-0', sm: 'py-2', normal: 'py-4', lg: 'py-6', xl: 'py-8' };
                        const sizeMap = { small: 'max-w-xs', medium: 'max-w-sm', large: 'max-w-md', full: 'w-full' };
                        const radiusMap = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', '3xl': 'rounded-3xl' };
                        const ratioMap = { auto: 'h-auto', square: 'aspect-square', wide: 'aspect-video', tall: 'aspect-[4/5]', landscape: 'aspect-[3/2]', portrait: 'aspect-[2/3]' };

                        const imgEl = s.image && imgPos !== 'none' && (
                          <img src={s.image} alt="" className={`object-cover ${ratioMap[meta.imageRatio || 'auto']} ${radiusMap[imgRadius]} ${imgPos === 'full-width' ? 'w-full h-32 shadow-xl' : imgPos === 'background' ? 'w-full' : sizeMap[imgSize] + ' mx-auto shadow-lg'} ${imgPos === 'full-width' ? 'h-32' : 'h-32'}`} />
                        );

                        const contentEl = (
                          <div>
                            {s.title && <h3 className="text-lg font-bold mb-2">{s.title}</h3>}
                            {s.subtitle && <p className="text-sm text-slate-400 mb-2">{s.subtitle}</p>}
                            {s.content && <div className="mt-2 text-sm opacity-80" dangerouslySetInnerHTML={{ __html: s.content || '' }} />}
                            {s.button_text && <span className="inline-block mt-3 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs">{s.button_text}</span>}
                          </div>
                        );

                        if (imgPos === 'background' && s.image) {
                          return (
                            <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} relative rounded-2xl overflow-hidden`}>
                              <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                              {meta.overlay && <div className="absolute inset-0 bg-black/50" style={{ opacity: (parseInt(meta.overlayOpacity || '50') / 100) }} />}
                              <div className={`relative z-10 ${meta.overlay ? 'text-white' : ''} p-6 ${alignMap[align]}`}>{contentEl}</div>
                            </div>
                          );
                        }
                        if (imgPos === 'left' || imgPos === 'right') {
                          return (
                            <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                {imgPos === 'left' ? <>{imgEl}{contentEl}</> : <>{contentEl}{imgEl}</>}
                              </div>
                            </div>
                          );
                        }
                        if ((imgPos === 'top' || imgPos === 'full-width') && s.image) {
                          return (
                            <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                              {imgEl}
                              <div className="mt-4">{contentEl}</div>
                            </div>
                          );
                        }
                        if (imgPos === 'bottom' && s.image) {
                          return (
                            <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                              {contentEl}
                              <div className="mt-4">{imgEl}</div>
                            </div>
                          );
                        }
                        return (
                          <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>{contentEl}</div>
                        );
                      })()}

                      {s.section_type === 'feature_grid' && (
                        <div className="py-6">
                          <h3 className="text-lg font-bold text-center mb-4">{s.title}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {featureItems.length === 0 && <div className="text-center text-xs text-slate-500 col-span-3">Add cards in Section Options.</div>}
                            {featureItems.map((item, i) => (
                              <div key={i} className="bg-slate-100/10 rounded-lg p-4 text-center">
                                <div className="text-2xl mb-2">{item.icon || '✨'}</div>
                                <div className="font-semibold">{item.title || 'Feature'}</div>
                                <div className="text-xs opacity-70">{item.desc || ''}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.section_type === 'image_text' && (() => {
                        const reverse = !!meta.reverse;
                        const split = meta.split || '50/50';
                        const align = meta.contentAlign || 'left';
                        const imgRadius = meta.imageRadius || '2xl';
                        const vAlign = meta.vAlign || 'center';
                        const imgShadow = meta.imageShadow || 'lg';
                        const splitMap = { '50/50': 'md:grid-cols-2', '60/40': 'md:grid-cols-[3fr_2fr]', '40/60': 'md:grid-cols-[2fr_3fr]', '70/30': 'md:grid-cols-[7fr_3fr]', '30/70': 'md:grid-cols-[3fr_7fr]' };
                        const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right', justify: 'text-justify' };
                        const radiusMap = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', '3xl': 'rounded-3xl' };
                        const shadowMap = { none: 'shadow-none', sm: 'shadow-sm', md: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-2xl' };
                        const vAlignMap = { top: 'items-start', center: 'items-center', bottom: 'items-end' };
                        const ratioMap = { auto: 'h-auto', square: 'aspect-square', wide: 'aspect-video', tall: 'aspect-[4/5]', landscape: 'aspect-[3/2]', portrait: 'aspect-[2/3]' };

                        const imgEl = s.image ? (
                          <img src={s.image} alt="" className={`w-full object-cover ${ratioMap[meta.imageRatio || 'auto']} ${radiusMap[imgRadius]} ${shadowMap[imgShadow]} ${meta.imageRatio === 'auto' || !meta.imageRatio ? 'h-40' : ''}`} />
                        ) : (
                          <div className={`w-full h-40 bg-slate-100/10 ${radiusMap[imgRadius]} flex items-center justify-center text-xs opacity-50`}>Image</div>
                        );

                        const textEl = (
                          <div className={alignMap[align]}>
                            <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                            {s.subtitle && <p className="text-sm text-slate-400 mb-2">{s.subtitle}</p>}
                            {s.content && <div className="mt-2 text-sm opacity-80" dangerouslySetInnerHTML={{ __html: s.content || '' }} />}
                            {s.button_text && <span className="inline-block mt-3 text-sm font-semibold text-red-500">{s.button_text}</span>}
                          </div>
                        );

                        return (
                          <div className={`grid grid-cols-1 ${splitMap[split]} gap-4 py-4 ${vAlignMap[vAlign]}`}>
                            {reverse ? <>{textEl}{imgEl}</> : <>{imgEl}{textEl}</>}
                          </div>
                        );
                      })()}

                      {s.section_type === 'stats' && (
                        <div className="py-6">
                          <h3 className="text-lg font-bold text-center mb-4">{s.title}</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                            {statsItems.length === 0 && <div className="text-xs text-slate-500 col-span-4">Add stats in Section Options.</div>}
                            {statsItems.map((stat, i) => (
                              <div key={i}>
                                <div className="text-2xl font-bold">{stat.value || '--'}</div>
                                <div className="text-xs opacity-60 uppercase">{stat.label || 'Label'}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.section_type === 'timeline' && (
                        <div className="py-6">
                          <h3 className="text-lg font-bold mb-4">{s.title}</h3>
                          <div className="border-l border-slate-200 ml-2">
                            {featureItems.length === 0 && <p className="text-xs text-slate-500 pl-4">Add milestones in Section Options.</p>}
                            {featureItems.map((item, i) => (
                              <div key={i} className="pl-4 pb-4 relative">
                                <div className="absolute -left-1 w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                                <p className="text-xs uppercase text-red-500 font-semibold">{item.year}</p>
                                <p className="font-semibold">{item.title}</p>
                                <p className="text-xs opacity-70">{item.desc}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.section_type === 'team' && (
                        <div className="py-6">
                          <h3 className="text-lg font-bold text-center mb-4">{s.title}</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {teamItems.length === 0 && <div className="text-xs text-slate-500 col-span-3 text-center">Add team members in Section Options.</div>}
                            {teamItems.map((person, i) => (
                              <div key={i} className="text-center">
                                <div className="h-20 w-20 rounded-full bg-slate-200 mx-auto mb-2 flex items-center justify-center text-slate-500">
                                  {person.name ? person.name.split(' ').map((n) => n[0]).join('') : 'TM'}
                                </div>
                                <p className="font-semibold">{person.name || 'Name'}</p>
                                <p className="text-xs text-red-500">{person.title || 'Role'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.section_type === 'faq' && (
                        <div className="py-6">
                          <h3 className="text-lg font-bold text-center mb-4">{s.title}</h3>
                          <div className="space-y-2">
                            {faqItems.length === 0 && <p className="text-xs text-slate-500 text-center">Add FAQ rows in Section Options.</p>}
                            {faqItems.map((faq, i) => (
                              <div key={i} className="border border-slate-200 rounded-lg p-3">
                                <p className="font-semibold">{faq.q || 'Question'}</p>
                                <p className="text-sm text-slate-500">{faq.a || 'Answer'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.section_type === 'cta' && (
                        <div className="text-center py-8">
                          {meta.badge && <span className="text-xs uppercase tracking-wide text-red-400">{meta.badge}</span>}
                          <h2 className="text-2xl font-bold">{s.title}</h2>
                          <div className="mt-2 text-sm opacity-80" dangerouslySetInnerHTML={{ __html: s.content || '' }} />
                          {s.button_text && <span className="inline-block mt-4 border border-current px-4 py-2 rounded-lg text-sm">{s.button_text}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right - Properties */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick Edit</h3>
            {!editingId && <p className="text-sm text-slate-500">Select a section in the canvas to edit its properties here.</p>}
            {editingId !== null && (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Title</label>
                  <input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 mt-1" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Content (HTML)</label>
                  <textarea value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 mt-1 font-mono" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Background</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {bgOptions.map((o) => (
                      <button
                        key={o.value}
                        onClick={() => setForm({ ...form, bg_color: o.value })}
                        className={`px-2 py-2 rounded-lg text-xs border ${form.bg_color === o.value ? 'border-red-500 text-red-400' : 'border-slate-700 text-slate-400'}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={(e) => saveSection(e)} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium transition">{editingId === 'new' ? 'Save' : 'Update'}</button>
                  <button onClick={() => resetForm()} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2 rounded-lg text-sm font-medium transition">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">All Sections</h3>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSelectedPage(s.page); editSection(s); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition ${s.page === selectedPage ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:bg-slate-800/50'}`}
                >
                  <span className="font-medium">{s.title || 'Untitled'}</span>
                  <span className="ml-2 text-slate-600">{s.page}</span>
                </button>
              ))}
              {sections.length === 0 && <p className="text-xs text-slate-600 py-2">No sections yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModernPageBuilder;
